<template>
  <div class="taxId">
    <div class="tax-input">
      <VTextField
        v-model="model"
        :mask="taxFormat.format"
        :rules="rules"
        :label="label"
        :hint="hint"
        :persistent-hint="persistentHint"
        :return-masked-value="taxFormat.returnMaskedValue"
        validate-on-blur
        class="flex-column"
        :required="required"
        @input="emitValue()"
        @blur="handleBlur"
      ></VTextField>
    </div>
  </div>
</template>

<script>
/**
 * Original Author: Ben Layton
 * Original Team: Fore oh Four!
 */
import { VTextField } from 'vuetify/lib';
import { taxFormats } from './taxIdFormats.js';
import Vue from 'vue';

/**
 * @typedef {Object} TaxFormatInfo
 * @prop {string} format
 * @prop {RegExp} regex
 * @prop {string} fieldInService
 * @prop {string} fieldInSAP
 * @prop {boolean} returnMaskedValue
 * @prop {(value: string) => boolean} [validationFunction]
 */

export default {
  name: 'NsTaxId',
  components: {
    VTextField,
  },
  props: {
    /**
     * Initial/current value
     */
    value: {
      type: [String, Object],
      default: '',
    },
    /**
     * Field Label
     */
    fieldLabel: {
      type: String,
      default: '',
    },
    /**
     * Hint
     */
    hint: {
      type: String,
      default: '',
    },
    persistentHint: {
      type: Boolean,
      default: true,
    },

    /**
     * Invalid message
     * @deprecated
     * @ignore
     */
    errorMessage: {
      type: String,
      default: '',
    },
    /**
     * Error message for when the field is required but no value is provided
     */
    requiredErrorMessage: {
      type: String,
      default: '',
    },
    /**
     * Error message for when the field's value is not formatted correctly
     */
    invalidFormatErrorMessage: {
      type: String,
      default: '',
    },
    /**
     * Default country code
     */
    country: {
      type: String,
      default: 'AR',
    },
    /**
     * Default customer type
     */
    custType: {
      type: String,
      default: 'personal',
    },
    required: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      model: this.value,
      blurred: false,
      rules: [
        (v) => {
          if (!v) {
            if (this.required) {
              return this.errors.required;
            }
            return true;
          }
          if (this.taxFormat.regex && !this.taxFormat.regex.test(v)) {
            return this.errors.format;
          }
          if (
            this.taxFormat.validationFunction &&
            !this.taxFormat.validationFunction(v)
          ) {
            return this.errors.format;
          }
          return true;
        },
      ],
    };
  },
  computed: {
    errors() {
      return {
        required:
          this.requiredErrorMessage ||
          this.errorMessage ||
          'This field is required',
        format:
          this.invalidFormatErrorMessage ||
          this.errorMessage ||
          'This value is not valid',
      };
    },
    /**
     * @returns {TaxFormatInfo}
     */
    taxFormat() {
      return taxFormats[this.country][this.custType].taxInfo;
    },
    label() {
      /* istanbul ignore next */
      return this.fieldLabel || this.taxFormat.label || 'Tax ID';
    },
  },
  watch: {
    model() {
      this.emitValue();
    },
    taxFormat() {
      this.emitValue();
    },
    value: {
      immediate: true,
      handler(value) {
        if (value && typeof value === 'object') {
          this.model = value.value;
        } else {
          this.model = value;
        }
      },
    },
    errorMessage: {
      immediate: true,
      handler(newval) {
        /* istanbul ignore if */
        if (newval && !Vue.config.silent) {
          console.warn(
            'Warning: NsTaxId.errorMessage is deprecated. Please use requiredErrorMessage and invalidFormatErrorMessage instead.'
          );
        }
      },
    },
  },
  methods: {
    emitValue() {
      this.$emit('input', {
        value: this.model,
        serviceField: this.taxFormat.fieldInService,
        sapField: this.taxFormat.fieldInSAP,
      });
    },
    handleBlur() {
      this.$emit('blur', {
        value: this.model,
      });
      this.emitValue();
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use '~@nuskin/ns-core-styles/src/scss/gridStyles';
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/forms";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
::v-deep {
  .tax-input {
    input[type='text']:focus {
      border: 1px solid map.get(colors.$theme, 'accent-color-b') !important;
    }
  }
}
</style>
